// Admin-only page for managing team members — roles and enabled/disabled status.
// Replaces the Firebase/Firestore version with Supabase queries.

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy]   = useState(false);

  async function load() {
    setBusy(true);
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[Users] load failed:', error.message);
    } else {
      // Normalise snake_case → camelCase for the table below.
      setUsers((data ?? []).map(u => ({
        id:          u.id,
        displayName: u.display_name,
        email:       u.email,
        phone:       u.phone,
        role:        u.role,
        disabled:    u.disabled
      })));
    }
    setBusy(false);
  }

  useEffect(() => { load(); }, []);

  async function toggleDisabled(u) {
    const { error } = await supabase
      .from('users')
      .update({ disabled: !u.disabled, updated_at: new Date().toISOString() })
      .eq('id', u.id);

    if (error) { console.error('[Users] toggleDisabled failed:', error.message); return; }
    load();
  }

  async function changeRole(u, role) {
    const { error } = await supabase
      .from('users')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('id', u.id);

    if (error) { console.error('[Users] changeRole failed:', error.message); return; }
    load();
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Team members</h1>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan="6" className="px-4 py-4 text-slate-500">Loading…</td>
              </tr>
            )}
            {!busy && users.map(u => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-2">{u.displayName}</td>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2">{u.phone}</td>
                <td className="px-4 py-2">
                  <select
                    className="input py-1"
                    value={u.role}
                    onChange={e => changeRole(u, e.target.value)}
                  >
                    <option value="sales">sales</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="px-4 py-2">
                  <span className={`chip ${u.disabled ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    {u.disabled ? 'Disabled' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => toggleDisabled(u)} className="btn-ghost">
                    {u.disabled ? 'Enable' : 'Disable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
